import { CanonicalSchema } from './types';

/** A concise field index for browsing an imported schema; the original schema stays authoritative. */
export const schemaFields = (document: Record<string, unknown>, prefix = ''): CanonicalSchema['fields'] => {
  const properties = document.properties as Record<string, Record<string, unknown>> | undefined;
  if (!properties || typeof properties !== 'object' || Array.isArray(properties)) return [];
  return Object.entries(properties).flatMap(([name, child]) => {
    if (!child || typeof child !== 'object') return [];
    const path = prefix ? `${prefix}.${name}` : name;
    const type = typeof child.type === 'string' ? child.type : 'any';
    const field = { path, type, required: Array.isArray(document.required) && document.required.includes(name) };
    const nested =
      type === 'array' && child.items && typeof child.items === 'object'
        ? schemaFields(child.items as Record<string, unknown>, `${path}[]`)
        : schemaFields(child, path);
    return [field, ...nested];
  });
};

export const schemaDocument = (schema?: CanonicalSchema): Record<string, unknown> | null => {
  if (!schema) return null;
  if (schema.jsonSchema) return schema.jsonSchema;
  const root: Record<string, unknown> = { type: 'object', properties: {}, required: [] };
  for (const field of [...schema.fields].sort(
    (left, right) => left.path.split('.').length - right.path.split('.').length,
  )) {
    const parts = field.path.split('.');
    let node = root;
    for (const [index, part] of parts.entries()) {
      const array = part.endsWith('[]');
      const key = array ? part.slice(0, -2) : part;
      if (!key || ['__proto__', 'constructor', 'prototype'].includes(key))
        throw new Error('Invalid schema field path.');
      node.type ??= 'object';
      node.properties ??= {};
      node.required ??= [];
      const properties = node.properties as Record<string, Record<string, unknown>>;
      const last = index === parts.length - 1;
      const child =
        properties[key] ??
        (properties[key] =
          last && !array
            ? {
                ...(field.type === 'any' ? {} : { type: field.type }),
                ...(field.type === 'array' ? { items: {} } : {}),
              }
            : array
              ? { type: 'array', items: { type: 'object', properties: {}, required: [] } }
              : { type: 'object', properties: {}, required: [] });
      if (field.required && !(node.required as string[]).includes(key)) (node.required as string[]).push(key);
      if (array) {
        child.type = 'array';
        child.items ??= {};
        if (last && field.type !== 'array' && field.type !== 'any')
          (child.items as Record<string, unknown>).type = field.type;
      }
      node = array ? (child.items as Record<string, unknown>) : child;
    }
  }
  return root;
};

export const inferSampleSchema = (samples: unknown[]): Record<string, unknown> => {
  const defined = samples.filter((value) => value !== undefined);
  if (defined.length === 0) return {};
  const typeOf = (value: unknown) => (value === null ? 'null' : Array.isArray(value) ? 'array' : typeof value);
  const types = [...new Set(defined.map(typeOf))];
  if (types.length > 1)
    return { anyOf: types.map((type) => inferSampleSchema(defined.filter((value) => typeOf(value) === type))) };
  if (types[0] === 'object') {
    const objects = defined as Record<string, unknown>[];
    const keys = new Set(objects.flatMap((value) => Object.keys(value)));
    return {
      type: 'object',
      properties: Object.fromEntries(
        [...keys].map((key) => [key, inferSampleSchema(objects.map((value) => value[key]))]),
      ),
    };
  }
  if (types[0] === 'array') return { type: 'array', items: inferSampleSchema((defined as unknown[][]).flat()) };
  return { type: types[0] };
};

/**
 * The same schema with this endpoint's must-have fields marked.
 *
 * An imported schema marks nothing as required, because the documents it came from mark nothing.
 * What a payload has to carry is a decision about the integration rather than a fact about the
 * API, so it is made per endpoint and applied here — which means the check runs where every other
 * check already runs, in the validator, with the field named in the error.
 *
 * A path into a list (`orderItems[].total`) marks the field required *within each element*, which
 * is what somebody ticking it means: not that there is a list, but that every entry carries it.
 */
export const withRequired = (
  document: Record<string, unknown> | null,
  paths: string[] | undefined,
): Record<string, unknown> | null => {
  if (!document || !paths?.length) return document;

  const copy = structuredClone(document) as Record<string, unknown>;
  for (const path of paths) {
    let node: Record<string, unknown> = copy;
    const parts = path.split('.').filter(Boolean);

    for (const [index, part] of parts.entries()) {
      const inList = part.endsWith('[]');
      const key = inList ? part.slice(0, -2) : part;
      if (['__proto__', 'constructor', 'prototype'].includes(key)) break;

      const properties = (node.properties ?? {}) as Record<string, Record<string, unknown>>;
      const child = properties[key];
      if (!child) break;

      if (index === parts.length - 1) {
        const required = Array.isArray(node.required) ? (node.required as string[]) : [];
        if (!required.includes(key)) node.required = [...required, key];
        break;
      }

      node = (inList ? ((child.items ?? {}) as Record<string, unknown>) : child) as Record<string, unknown>;
    }
  }

  return copy;
};
